import { useState, useMemo, useEffect } from "react";
import "./StudentManagement.css";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, authFetch } from "../../../config/api";

const TABS = ["Edit", "Remove"];

const StudentManagement = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("Edit");

  // Shared search fields
  const [regNo, setRegNo] = useState("");
  const [session, setSession] = useState("");
  const [semester, setSemester] = useState("");

  // Edit fields
  const [fullname, setFullname] = useState("");
  const [moe, setMOE] = useState("");
  const [level, setLevel] = useState("");

  // Remove fields
  const [courseCode, setCourseCode] = useState("");

  const [message, setMessage] = useState({ text: "", type: "" });
  const [student, setStudent] = useState(null);
  const [semesterResult, setSemesterResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    authFetch(`${API_BASE_URL}/api/sessions/`)
      .then((r) => r.json())
      .then((d) => { if (d.sessions) setSessions(d.sessions.map((s) => s.session)); })
      .catch(() => {});
  }, []);

  const [scoreModal, setScoreModal] = useState({ open: false, course: null, total: "" });
  const [confirmAction, setConfirmAction] = useState({ open: false, title: "", description: "", onConfirm: null });

  const request = async (endpoint, body, method) => {
    setLoading(true);
    setMessage({ text: "", type: "" });
    try {
      const res = await authFetch(`${API_BASE_URL}/api/student/${endpoint}`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setMessage({
        text: data.message || (res.ok ? "Done." : "Request failed."),
        type: res.ok ? "success" : "error",
      });
      if (data.student) setStudent(data.student);
      if (data.semester_result) setSemesterResult(data.semester_result);
    } catch {
      setMessage({ text: "Network error.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (title, description, onConfirm) =>
    setConfirmAction({ open: true, title, description, onConfirm });
  const closeConfirm = () =>
    setConfirmAction({ open: false, title: "", description: "", onConfirm: null });

  const openScoreModal = (course) =>
    setScoreModal({ open: true, course, total: course?.total ?? "" });
  const closeScoreModal = () =>
    setScoreModal({ open: false, course: null, total: "" });

  const updateCourseTotal = async () => {
    if (!scoreModal.course) return;
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/student/update-course-total`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reg_no: regNo,
          session,
          semester,
          course_code: scoreModal.course.course_code,
          total: scoreModal.total,
        }),
      });
      const data = await res.json();
      setMessage({ text: data.message || "Score updated.", type: res.ok ? "success" : "error" });
      if (res.ok && semesterResult) {
        setSemesterResult({
          ...semesterResult,
          courses: semesterResult.courses.map((c) =>
            c.course_code === scoreModal.course.course_code ? { ...c, ...data.course } : c
          ),
          gpa: data.gpa ?? semesterResult.gpa,
          session_gpa: data.session_gpa ?? semesterResult.session_gpa,
        });
      }
    } catch {
      setMessage({ text: "Network error.", type: "error" });
    } finally {
      setLoading(false);
      closeScoreModal();
    }
  };

  const hasStudent = Boolean(student?._id);

  const statusLines = useMemo(() => {
    if (!message.text) return [];
    const parts = message.text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    return parts.length > 1 ? parts : [message.text];
  }, [message.text]);

  return (
    <div className="sm_page">
      {/* Header */}
      <div className="sm_header">
        <div>
          <p className="sm_overline">Admin Tools</p>
          <h2>Student Management</h2>
          <p className="sm_subtitle">Search, edit, and remove student records.</p>
        </div>
      </div>

      <div className="sm_body">
        {/* Left: search + actions */}
        <div className="sm_main">
          {/* Search bar */}
          <div className="sm_search_row">
            <div className="sm_search_field">
              <label>Reg Number</label>
              <input
                type="text"
                placeholder="e.g. 2018/123456"
                value={regNo}
                onChange={(e) => setRegNo(e.target.value)}
              />
            </div>
            <div className="sm_search_field">
              <label>Session</label>
              <select value={session} onChange={(e) => setSession(e.target.value)}>
                <option value="">—</option>
                {sessions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="sm_search_field sm_search_field--sm">
              <label>Semester</label>
              <select value={semester} onChange={(e) => setSemester(e.target.value)}>
                <option value="">—</option>
                <option value="1">1st</option>
                <option value="2">2nd</option>
              </select>
            </div>
            <div className="sm_search_btns">
              <button
                className="sm_btn sm_btn--primary"
                onClick={() => {
                  setStudent(null);
                  setSemesterResult(null);
                  request("search", { reg_no: regNo }, "POST");
                }}
                disabled={loading || !regNo.trim()}
              >
                Find Student
              </button>
              <button
                className="sm_btn"
                onClick={() => {
                  setSemesterResult(null);
                  request("semester-result", { reg_no: regNo, session, semester }, "POST");
                }}
                disabled={loading || !regNo.trim() || !session || !semester}
              >
                Load Semester
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="sm_tabs">
            {TABS.map((t) => (
              <button
                key={t}
                className={`sm_tab ${tab === t ? "active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Tab: Edit */}
          {tab === "Edit" && (
            <div className="sm_panel">
              <div className="sm_fields_row">
                <div className="sm_field">
                  <label>Full Name</label>
                  <input
                    type="text"
                    placeholder="New full name"
                    value={fullname}
                    onChange={(e) => setFullname(e.target.value)}
                  />
                </div>
                <button
                  className="sm_btn sm_btn--inline"
                  disabled={loading || !regNo.trim() || !fullname.trim()}
                  onClick={() => request("update-name", { reg_no: regNo, fullname }, "PUT")}
                >
                  Update Name
                </button>
              </div>

              <div className="sm_fields_row">
                <div className="sm_field">
                  <label>Mode of Entry</label>
                  <input
                    type="text"
                    placeholder="UTME / Direct Entry"
                    value={moe}
                    onChange={(e) => setMOE(e.target.value)}
                  />
                </div>
                <button
                  className="sm_btn sm_btn--inline"
                  disabled={loading || !regNo.trim() || !moe.trim()}
                  onClick={() => request("moe/update", { reg_no: regNo, moe }, "PUT")}
                >
                  Update MOE
                </button>
              </div>

              <div className="sm_fields_row">
                <div className="sm_field">
                  <label>Semester Level</label>
                  <input
                    type="text"
                    placeholder="e.g. 300"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                  />
                </div>
                <button
                  className="sm_btn sm_btn--inline"
                  disabled={loading || !regNo.trim() || !session || !semester || !level}
                  onClick={() =>
                    request("update-semester-level", { reg_no: regNo, session, semester, level }, "PUT")
                  }
                >
                  Update Level
                </button>
              </div>
            </div>
          )}

          {/* Tab: Remove */}
          {tab === "Remove" && (
            <div className="sm_panel">
              <div className="sm_remove_grid">
                <button
                  className="sm_remove_btn"
                  disabled={loading || !regNo.trim() || !session || !semester}
                  onClick={() =>
                    openConfirm(
                      "Remove Semester",
                      `Delete ${session} semester ${semester} for ${regNo}?`,
                      () => request("remove-semester", { reg_no: regNo, session, semester }, "DELETE")
                    )
                  }
                >
                  <span className="sm_remove_label">Remove Semester</span>
                  <span className="sm_remove_hint">Deletes one semester's results</span>
                </button>

                <button
                  className="sm_remove_btn"
                  disabled={loading || !regNo.trim() || !session}
                  onClick={() =>
                    openConfirm(
                      "Remove Session",
                      `Delete all results for ${session} for ${regNo}?`,
                      () => request("remove-session", { reg_no: regNo, session }, "DELETE")
                    )
                  }
                >
                  <span className="sm_remove_label">Remove Session</span>
                  <span className="sm_remove_hint">Deletes all semesters in a session</span>
                </button>

                <button
                  className="sm_remove_btn"
                  disabled={loading || !regNo.trim() || !session || !semester || !courseCode.trim()}
                  onClick={() =>
                    openConfirm(
                      "Remove Course",
                      `Delete ${courseCode} from ${session} sem ${semester} for ${regNo}?`,
                      () =>
                        request("remove-course", { reg_no: regNo, session, semester, course_code: courseCode }, "DELETE")
                    )
                  }
                >
                  <span className="sm_remove_label">Remove Course</span>
                  <span className="sm_remove_hint">
                    <input
                      type="text"
                      placeholder="Course code e.g. PCT224"
                      value={courseCode}
                      onChange={(e) => setCourseCode(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="sm_inline_input"
                    />
                  </span>
                </button>

                <button
                  className="sm_remove_btn sm_remove_btn--danger"
                  disabled={loading || !regNo.trim()}
                  onClick={() =>
                    openConfirm(
                      "Remove Student",
                      `Permanently delete ${regNo} and all associated records? This cannot be undone.`,
                      () => request("remove-student", { reg_no: regNo }, "DELETE")
                    )
                  }
                >
                  <span className="sm_remove_label">Remove Student</span>
                  <span className="sm_remove_hint">Permanently deletes student + all results</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: result panel */}
        <div className="sm_side">
          {message.text && (
            <div className={`sm_status ${message.type}`}>
              <p className="sm_status_label">{message.type === "success" ? "Success" : "Error"}</p>
              <ul>
                {statusLines.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>
          )}

          {hasStudent && (
            <div className="sm_result_card">
              <p className="sm_result_label">Student</p>
              <p className="sm_result_name">{student.fullname}</p>
              <p className="sm_result_meta">{student.reg_no} · Level {student.level}</p>
              {student.moe && <p className="sm_result_meta">MOE: {student.moe}</p>}
              {semesterResult && (
                <div className="sm_gpa_row">
                  <span>{semesterResult.session} · Sem {semesterResult.semester}</span>
                  <span>GPA {semesterResult.gpa?.toFixed?.(2) ?? "—"}</span>
                </div>
              )}
              <button
                className="sm_btn sm_btn--full"
                onClick={() => navigate(`/admin/student/${student._id}`)}
              >
                Open Full Profile
              </button>
            </div>
          )}

          {semesterResult?.courses?.length > 0 && (
            <div className="sm_result_card">
              <p className="sm_result_label">Courses — click to edit score</p>
              <div className="sm_course_list">
                {semesterResult.courses.map((course) => (
                  <button
                    key={course.course_code}
                    className="sm_course_row"
                    onClick={() => openScoreModal(course)}
                  >
                    <span className="sm_course_code">{course.course_code.toUpperCase()}</span>
                    <span className="sm_course_score">{Number(course.total || 0).toFixed(0)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirm modal */}
      {confirmAction.open && (
        <div className="sm_overlay" role="dialog" aria-modal="true">
          <div className="sm_modal">
            <h3>{confirmAction.title}</h3>
            <p>{confirmAction.description}</p>
            <div className="sm_modal_actions">
              <button className="sm_btn" onClick={closeConfirm}>Cancel</button>
              <button
                className="sm_btn sm_btn--danger"
                onClick={() => { const fn = confirmAction.onConfirm; closeConfirm(); fn?.(); }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Score modal */}
      {scoreModal.open && (
        <div className="sm_overlay" role="dialog" aria-modal="true">
          <div className="sm_modal">
            <h3>Update Score</h3>
            <p>{scoreModal.course?.course_code?.toUpperCase()} · {scoreModal.course?.course_title}</p>
            <div className="sm_field" style={{ margin: "1rem 0" }}>
              <label>Total Score (0–100)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={scoreModal.total}
                onChange={(e) => setScoreModal((p) => ({ ...p, total: e.target.value }))}
              />
            </div>
            <div className="sm_modal_actions">
              <button className="sm_btn" onClick={closeScoreModal}>Cancel</button>
              <button className="sm_btn sm_btn--primary" onClick={updateCourseTotal} disabled={loading}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement;
